import { useState, useEffect, useCallback } from 'react';
import { Modal, ModalBody, ModalFooter, Button, Spinner } from 'reactstrap';
import { Search, Award, RotateCcw } from 'react-feather';
import profesoresApiService from '../../services/http/profesoresApiService';
import ModalClickable from '../../layouts/modals/ModalClickable';
import Loader from '../../layouts/loader/Loader';
import logger from '../../services/logger';
import colors from '../../theme/colors';

// La asignación de director es por SEDE: el backend espera empresaId 'CEDESCA' | 'CNTEC'.
// Si en el futuro pasara a ser por idcentro, habría que añadir un selector de centro y
// enviar idcentro en asignarDirector.
const SEDES = ['CEDESCA', 'CNTEC'];
const PAGE_SIZE = 10;

const norm = (value) => (value ?? '').toString().toLowerCase();

const thStyle = {
  textAlign: 'left',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.textMuted,
  padding: '10px 14px',
};

const tdStyle = {
  padding: '11px 14px',
  verticalAlign: 'middle',
};

const AsignarDirector = () => {
  const [sede, setSede] = useState('CEDESCA');
  const [profesores, setProfesores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [confirmProf, setConfirmProf] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [page, setPage] = useState(1);

  const fetchProfesores = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await profesoresApiService.obtenerProfesores({ centro: sede });
      setProfesores(Array.isArray(data) ? data : []);
    } catch (error) {
      logger.error('Error al obtener profesores:', error);
      setProfesores([]);
    } finally {
      setIsLoading(false);
    }
  }, [sede]);

  useEffect(() => {
    fetchProfesores();
  }, [fetchProfesores]);

  const directorActual = profesores.find((p) => p.Director === 1);

  const confirmarAsignacion = async () => {
    setIsAssigning(true);
    try {
      const res = await profesoresApiService.asignarDirector(
        { empresaId: sede, indice: Number(confirmProf.Codigo) },
        { silent: true },
      );
      setConfirmProf(null);
      setFeedback({
        color: 'primary',
        header: res?.message || 'Director asignado correctamente.',
      });
      await fetchProfesores();
    } catch (error) {
      const msg = error?.response?.data?.error || 'No se pudo asignar el director.';
      setConfirmProf(null);
      setFeedback({ color: 'danger', header: msg });
    } finally {
      setIsAssigning(false);
    }
  };

  const filtrados = profesores.filter((p) => {
    if (!filtro.trim()) return true;
    const q = norm(filtro);
    return (
      norm(p.Nombre).includes(q) ||
      norm(p.Apellidos).includes(q) ||
      norm(p['Email personal']).includes(q) ||
      norm(p.Codigo).includes(q)
    );
  });

  // Reset de página al cambiar de sede o filtro
  useEffect(() => {
    setPage(1);
  }, [sede, filtro]);

  const totalPages = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const inicio = (pageSafe - 1) * PAGE_SIZE;
  const visibles = filtrados.slice(inicio, inicio + PAGE_SIZE);

  const nombreCompleto = (p) => `${p.Nombre || ''} ${p.Apellidos || ''}`.trim() || '—';

  return (
    <div>
      <p style={{ color: colors.textMuted, marginTop: 0, marginBottom: '16px', fontSize: '0.8rem' }}>
        Designa el director de cada sede. Solo puede haber un director por sede.
      </p>

      {/* Controles */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: '180px' }}>
          <label
            htmlFor="director-sede"
            style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: '6px',
            }}
          >
            Sede
          </label>
          <select
            id="director-sede"
            className="form-control"
            value={sede}
            onChange={(e) => {
              setSede(e.target.value);
              setFiltro('');
            }}
          >
            {SEDES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <label
            htmlFor="director-filtro"
            style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: '6px',
            }}
          >
            Buscar
          </label>
          <div style={{ position: 'relative' }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted,
              }}
            />
            <input
              id="director-filtro"
              type="text"
              className="form-control"
              placeholder="Nombre, apellidos, email o código"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              style={{ paddingLeft: '32px' }}
            />
          </div>
        </div>
        <Button color="light" onClick={fetchProfesores} title="Recargar listado" disabled={isLoading}>
          <RotateCcw size={15} />
        </Button>
      </div>

      {/* Director actual */}
      {!isLoading && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 14px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bg,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.875rem',
          }}
        >
          <Award size={16} style={{ color: directorActual ? colors.primary : colors.textMuted }} />
          {directorActual ? (
            <span>
              Director actual de {sede}: <strong>{nombreCompleto(directorActual)}</strong>{' '}
              <span style={{ color: colors.textMuted }}>(#{directorActual.Codigo})</span>
            </span>
          ) : (
            <span style={{ color: colors.textSecondary }}>Sin director asignado en {sede}.</span>
          )}
        </div>
      )}

      {/* Listado */}
      {isLoading ? (
        <Loader />
      ) : (
        <div style={{ marginTop: '16px', overflowX: 'auto' }}>
          {filtrados.length === 0 ? (
            <div
              style={{
                padding: '32px',
                textAlign: 'center',
                color: colors.textMuted,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                background: colors.bgCard,
              }}
            >
              No hay profesores que coincidan.
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                fontSize: '0.875rem',
              }}
            >
              <thead>
                <tr style={{ background: colors.bg }}>
                  <th style={thStyle}>Código</th>
                  <th style={thStyle}>Profesor</th>
                  <th style={thStyle}>Email</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((p) => {
                  const esDirector = p.Director === 1;
                  return (
                    <tr key={p.Codigo} style={{ borderTop: `1px solid ${colors.border}` }}>
                      <td style={{ ...tdStyle, color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                        #{p.Codigo}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{nombreCompleto(p)}</td>
                      <td style={{ ...tdStyle, color: colors.textSecondary }}>
                        {p['Email personal'] || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {esDirector ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '4px 10px',
                              borderRadius: '999px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              background: colors.primaryLight,
                              color: colors.primary,
                            }}
                          >
                            <Award size={13} /> Director actual
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmProf(p)}
                            style={{
                              padding: '6px 12px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              borderRadius: '6px',
                              border: `1px solid ${colors.primary}`,
                              background: colors.bgCard,
                              color: colors.primary,
                              cursor: 'pointer',
                            }}
                          >
                            Hacer director
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Paginación */}
          {filtrados.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '12px',
                fontSize: '0.8rem',
                color: colors.textSecondary,
              }}
            >
              <span>
                {inicio + 1}–{Math.min(inicio + PAGE_SIZE, filtrados.length)} de {filtrados.length}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={pageSafe <= 1}
                >
                  Anterior
                </Button>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {pageSafe} / {totalPages}
                </span>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={pageSafe >= totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmación */}
      <Modal isOpen={confirmProf !== null} centered toggle={() => !isAssigning && setConfirmProf(null)}>
        <ModalBody>
          {isAssigning ? (
            <h4 className="text-center mt-3">
              <Spinner size="sm" /> Asignando director...
            </h4>
          ) : (
            <div className="text-center mt-3">
              <h4>¿Hacer director a {confirmProf ? nombreCompleto(confirmProf) : ''}?</h4>
              <p style={{ color: colors.textSecondary, marginBottom: 0 }}>
                Se quitará el director actual de {sede} y se asignará este profesor.
              </p>
            </div>
          )}
        </ModalBody>
        {!isAssigning && (
          <ModalFooter style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button color="secondary" onClick={() => setConfirmProf(null)}>
              Cancelar
            </Button>
            <Button color="primary" onClick={confirmarAsignacion}>
              Hacer director
            </Button>
          </ModalFooter>
        )}
      </Modal>

      {/* Resultado */}
      {feedback && (
        <ModalClickable
          key={feedback.header}
          header={feedback.header}
          color={feedback.color}
          function={() => setFeedback(null)}
        />
      )}
    </div>
  );
};

export default AsignarDirector;
